import { DEFAULT_LAYOUT_ID, layoutOf, relocateLayout, blockComponent, formPages, type RelocationReport } from '@ayumia/component-runtime';
import type { InspectionTemplateBody } from '@ayumia/contracts';
import { blockOf, componentByName, layoutById, type EditorRegistry } from './layout-registry';

/**
 * Opérations de l'éditeur 1 sur la couche de mise en page d'un gabarit (ADR-146 §2, §5) : gabarit et
 * navigation, pages, placements des parties, blocs, totaux, confirmation, écran de succès. Pures : chaque
 * opération rend un nouveau gabarit (annuler et rétablir par l'historique du socle). Elles ne touchent
 * jamais un champ, une cible, une preuve ni une règle : la couche ne fait que placer et présenter.
 */

export type Template = InspectionTemplateBody;
export type FormLayout = NonNullable<Template['layout']>;
export type LayoutPage = NonNullable<FormLayout['pages']>[number];
export type Placement = NonNullable<FormLayout['placements']>[string];
export type Block = NonNullable<FormLayout['blocks']>[number];
export type Aggregate = NonNullable<FormLayout['aggregates']>[number];
export type Navigation = NonNullable<FormLayout['navigation']>;
type Localized = Record<string, string>;
/** Changement partiel : une clé à `undefined` retire le réglage (exactOptionalPropertyTypes). */
export type Patch<T> = { [K in keyof T]?: T[K] | undefined };

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Retire les clés `undefined` (une valeur effacée disparaît du JSON). */
function compact<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

/** Applique un changement à la couche (créée vide si le gabarit n'en a pas) ; une couche vide disparaît. */
export function updateLayout(template: Template, change: (layout: FormLayout) => object): Template {
  const next = compact(change(clone(layoutOf(template) ?? ({} as FormLayout))) as FormLayout);
  const copy = { ...template } as Template & { layout?: FormLayout };
  if (Object.keys(next).length === 0) delete copy.layout;
  else copy.layout = next;
  return copy;
}

/** Identifiant interne tiré d'un libellé (« Poids brut » → `poids_brut`), jamais montré, unique dans `used`. */
export function slugId(label: string, used: ReadonlySet<string>, prefix: string): string {
  const base = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  const start = /^[a-z]/.test(base) ? base : `${prefix}${base === '' ? '' : `_${base}`}`;
  if (!used.has(start)) return start;
  let index = 2;
  while (used.has(`${start}_${index}`)) index += 1;
  return `${start}_${index}`;
}

// ---- Gabarit et navigation (§5.2).

/**
 * Change le gabarit de mise en page : chaque partie et chaque bloc suit le rôle de sa région ; ce qui n'a
 * pas de place va dans la région de dépôt et figure au rapport. Rien n'est supprimé (calcul du Core).
 */
export function chooseLayout(template: Template, registry: EditorRegistry, layoutId: string): { template: Template; report: RelocationReport } {
  const from = layoutById(registry, layoutOf(template)?.layout_ref) ?? layoutById(registry, DEFAULT_LAYOUT_ID);
  const to = layoutById(registry, layoutId);
  if (from === undefined || to === undefined) throw new Error('Mise en page introuvable dans le registre.');
  const categoryOf = (block: { component: string }): string | undefined => blockOf(componentByName(registry, blockComponent(block).name))?.category;
  const report = relocateLayout(template, { artifact_id: from.artifact_id, body: from.body }, { artifact_id: to.artifact_id, body: to.body }, categoryOf);
  return { template: { ...template, layout: report.layout } as Template, report };
}

/** Pose la région d'un élément resté sans place après un changement de gabarit. */
export function placeElement(template: Template, element: { kind: 'section' | 'block'; id: string }, region: string): Template {
  return element.kind === 'section' ? setPlacement(template, element.id, { region }) : updateBlock(template, element.id, { region });
}

/** Mode de navigation et règles par format ; une règle `undefined` est retirée. */
export function setNavigation(template: Template, patch: Patch<Navigation>): Template {
  return updateLayout(template, (layout) => {
    const navigation = compact({ ...(layout.navigation ?? { mode: 'single' }), ...patch } as Navigation);
    return { ...layout, navigation };
  });
}

/** Réglage simple de la couche : nature, densité, pied, confirmation, succès. */
export function setLayoutField<K extends 'intent' | 'density' | 'footer' | 'confirmation' | 'success'>(template: Template, key: K, value: FormLayout[K] | undefined): Template {
  return updateLayout(template, (layout) => ({ ...layout, [key]: value }));
}

// ---- Pages (§2.3).

export const declaredPages = (template: Template): LayoutPage[] => layoutOf(template)?.pages ?? [];

type PageDraft = Omit<LayoutPage, 'sections'> & { sections: string[] };

function withPages(layout: FormLayout, pages: readonly PageDraft[]): FormLayout {
  const kept = pages.filter((page) => page.sections.length > 0);
  return { ...layout, pages: kept.length === 0 ? undefined : kept } as unknown as FormLayout;
}

/**
 * Nouvelle page avec ses parties (au moins une) : elles quittent leur ancienne page ; une page vidée est
 * retirée. Les parties citées nulle part s'affichent sur la dernière page (règle du Core).
 */
export function addPage(template: Template, label: Localized, sections: readonly string[], id?: string): { template: Template; id: string } {
  const pages = declaredPages(template);
  const pageId = id ?? slugId(label['fr'] ?? 'page', new Set(pages.map((page) => page.page_id)), 'page');
  const moved = new Set(sections);
  const next = updateLayout(template, (layout) => withPages(layout, [...pages.map((page) => ({ ...page, sections: page.sections.filter((section) => !moved.has(section)) })), { page_id: pageId, label, sections: [...sections] }]));
  return { template: next, id: pageId };
}

export function updatePage(template: Template, pageId: string, patch: Patch<Omit<LayoutPage, 'page_id' | 'sections'>>): Template {
  return updateLayout(template, (layout) => withPages(layout, declaredPages(template).map((page) => (page.page_id === pageId ? (compact({ ...page, ...patch }) as PageDraft) : page))));
}

/** Retire une page : ses parties rejoignent la page précédente (la suivante pour la première). */
export function removePage(template: Template, pageId: string): Template {
  const pages = declaredPages(template);
  const index = pages.findIndex((page) => page.page_id === pageId);
  if (index < 0) return template;
  const removed = pages[index]!;
  const rest = pages.filter((page) => page.page_id !== pageId);
  const heir = rest[Math.max(0, index - 1)];
  return updateLayout(template, (layout) => {
    const next = withPages(layout, rest.map((page) => (page === heir ? { ...page, sections: [...page.sections, ...removed.sections] } : page)));
    // Un bloc qui ne paraissait que sur cette page devient persistant plutôt que de citer une page absente.
    const blocks = (layout.blocks ?? []).map((block) => {
      if (block.page_ids === undefined) return block;
      const kept = block.page_ids.filter((id) => id !== pageId);
      return compact({ ...block, page_ids: kept.length === 0 ? undefined : kept });
    });
    return { ...next, blocks: blocks.length === 0 ? undefined : blocks } as FormLayout;
  });
}

export function movePage(template: Template, pageId: string, delta: -1 | 1): Template {
  const pages = [...declaredPages(template)];
  const index = pages.findIndex((page) => page.page_id === pageId);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= pages.length) return template;
  const [page] = pages.splice(index, 1);
  pages.splice(target, 0, page!);
  return updateLayout(template, (layout) => withPages(layout, pages));
}

/**
 * Place une page à une position de la liste (glisser-déposer dans la barre d'onglets ou les calques) :
 * `index` est sa place une fois retirée de la liste. Rien ne change si elle y est déjà.
 */
export function movePageTo(template: Template, pageId: string, index: number): Template {
  const pages = [...declaredPages(template)];
  const from = pages.findIndex((page) => page.page_id === pageId);
  if (from < 0) return template;
  const [page] = pages.splice(from, 1);
  const at = Math.max(0, Math.min(index, pages.length));
  if (at === from) return template;
  pages.splice(at, 0, page!);
  return updateLayout(template, (layout) => withPages(layout, pages));
}

/** Déplace une partie vers une page (à la fin). */
export function moveSectionToPage(template: Template, sectionId: string, pageId: string): Template {
  return updateLayout(template, (layout) =>
    withPages(layout, declaredPages(template).map((page) => {
      const sections = page.sections.filter((section) => section !== sectionId);
      return page.page_id === pageId ? { ...page, sections: [...sections, sectionId] } : { ...page, sections };
    })),
  );
}

/** Page qui affiche une partie (pages effectives du Core). */
export function pageOfSection(template: Template, sectionId: string): string | undefined {
  return formPages(template).find((page) => page.sections.includes(sectionId))?.page_id;
}

// ---- Placements des parties (§2.4).

export function setPlacement(template: Template, sectionId: string, patch: Patch<Placement>): Template {
  return updateLayout(template, (layout) => {
    const placements = { ...(layout.placements ?? {}) };
    const merged = compact({ ...(placements[sectionId] ?? {}), ...patch }) as Placement;
    if (Object.keys(merged).length === 0) delete placements[sectionId];
    else placements[sectionId] = merged;
    return { ...layout, placements: Object.keys(placements).length === 0 ? undefined : placements } as FormLayout;
  });
}

// ---- Blocs (§2.5).

export const blocksOf = (template: Template): Block[] => layoutOf(template)?.blocks ?? [];

function withBlocks(layout: FormLayout, blocks: Block[]): FormLayout {
  return { ...layout, blocks: blocks.length === 0 ? undefined : blocks } as FormLayout;
}

/**
 * Pose un bloc du registre dans une région, à la position donnée parmi les blocs (fin par défaut). Sa
 * plage suit les versions MINOR du composant (`^x.y.z`). Le contrôle de la recette est fait par l'appelant
 * (`refusalOf`) avant d'appliquer.
 */
export function insertBlock(template: Template, registry: EditorRegistry, componentName: string, region: string, options: { id?: string; label?: Localized; index?: number; page_ids?: string[] } = {}): { template: Template; id: string } {
  const component = componentByName(registry, componentName);
  if (component === undefined) throw new Error('Bloc introuvable dans la bibliothèque.');
  const blocks = blocksOf(template);
  const label = options.label ?? (component.manifest.component.label as Localized | undefined);
  const id = options.id ?? slugId(label?.['fr'] ?? componentName, new Set(blocks.map((block) => block.block_id)), 'bloc');
  const block = compact({ block_id: id, ...(options.label === undefined ? {} : { label: options.label }), component: `${component.name}@^${component.version}`, region, page_ids: options.page_ids } as Block);
  const at = options.index === undefined ? blocks.length : Math.max(0, Math.min(options.index, blocks.length));
  return { template: updateLayout(template, (layout) => withBlocks(layout, [...blocks.slice(0, at), block, ...blocks.slice(at)])), id };
}

/** Change un bloc ; une valeur `undefined` retire le réglage. */
export function updateBlock(template: Template, blockId: string, patch: Patch<Block>): Template {
  return updateLayout(template, (layout) => withBlocks(layout, blocksOf(template).map((block) => (block.block_id === blockId ? (compact({ ...block, ...patch }) as Block) : block))));
}

/** Propriété exposée d'un bloc (onglet Contenu) ; `undefined` la retire. */
export function setBlockProp(template: Template, blockId: string, name: string, value: unknown): Template {
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
  if (block === undefined) return template;
  const props = compact({ ...(block.props ?? {}), [name]: value } as Record<string, unknown>);
  return updateBlock(template, blockId, { props: (Object.keys(props).length === 0 ? undefined : props) as Block['props'] });
}

/** Liaison d'une entrée d'un bloc (onglet Données), par chemin choisi dans une liste ; `undefined` la retire. */
export function setBlockBinding(template: Template, blockId: string, name: string, path: string | undefined): Template {
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
  if (block === undefined) return template;
  const bind = compact({ ...(block.bind ?? {}), [name]: path === undefined ? undefined : { path } } as Record<string, { path: string } | undefined>);
  return updateBlock(template, blockId, { bind: (Object.keys(bind).length === 0 ? undefined : bind) as Block['bind'] });
}

export function removeBlock(template: Template, blockId: string): Template {
  return updateLayout(template, (layout) => {
    const footer = layout.footer === undefined ? undefined : compact(Object.fromEntries(Object.entries(layout.footer).map(([slot, value]) => [slot, value === blockId ? undefined : value])) as NonNullable<FormLayout['footer']>);
    return { ...withBlocks(layout, blocksOf(template).filter((block) => block.block_id !== blockId)), footer: footer !== undefined && Object.keys(footer).length > 0 ? footer : undefined } as FormLayout;
  });
}

/** Copie d'un bloc placée juste après lui, avec un nouvel identifiant interne. */
export function duplicateBlock(template: Template, blockId: string): { template: Template; id: string } {
  const blocks = blocksOf(template);
  const index = blocks.findIndex((block) => block.block_id === blockId);
  if (index < 0) return { template, id: blockId };
  const source = blocks[index]!;
  const id = slugId(source.block_id, new Set(blocks.map((block) => block.block_id)), 'bloc');
  const copy = { ...clone(source), block_id: id, ...(source.label === undefined ? {} : { label: Object.fromEntries(Object.entries(source.label as Localized).map(([language, text]) => [language, `${text} (copie)`])) }) } as Block;
  return { template: updateLayout(template, (layout) => withBlocks(layout, [...blocks.slice(0, index + 1), copy, ...blocks.slice(index + 1)])), id };
}

/**
 * Replace un bloc existant à un point du gabarit (glisser-déposer) : même bloc (identifiant, réglages,
 * liaisons), nouvelle région, nouvelle position parmi les blocs, pages données. `page_ids` à `undefined`
 * rend le bloc présent sur toutes les pages. Le contrôle de la recette est fait par l'appelant.
 */
export function placeBlock(template: Template, blockId: string, spot: { region: string; index?: number; page_ids?: string[] | undefined }): Template {
  const blocks = blocksOf(template);
  const block = blocks.find((candidate) => candidate.block_id === blockId);
  if (block === undefined) return template;
  const rest = blocks.filter((candidate) => candidate !== block);
  const moved = compact({ ...block, region: spot.region, page_ids: spot.page_ids === undefined ? undefined : [...spot.page_ids] } as Block);
  const at = spot.index === undefined ? rest.length : Math.max(0, Math.min(spot.index, rest.length));
  return updateLayout(template, (layout) => withBlocks(layout, [...rest.slice(0, at), moved, ...rest.slice(at)]));
}

/** Gabarit sans un bloc, pour calculer une position d'arrivée (aucune référence du pied touchée). */
export function withoutBlock(template: Template, blockId: string): Template {
  return updateLayout(template, (layout) => withBlocks(layout, blocksOf(template).filter((block) => block.block_id !== blockId)));
}

/** Monte ou descend un bloc parmi ceux de sa région. */
export function moveBlock(template: Template, blockId: string, delta: -1 | 1): Template {
  const blocks = [...blocksOf(template)];
  const index = blocks.findIndex((block) => block.block_id === blockId);
  if (index < 0) return template;
  const region = blocks[index]!.region;
  let target = index + delta;
  while (target >= 0 && target < blocks.length && blocks[target]!.region !== region) target += delta;
  if (target < 0 || target >= blocks.length) return template;
  const [block] = blocks.splice(index, 1);
  blocks.splice(target, 0, block!);
  return updateLayout(template, (layout) => withBlocks(layout, blocks));
}

// ---- Totaux (§2.7).

export const aggregatesOf = (template: Template): Aggregate[] => layoutOf(template)?.aggregates ?? [];

export function addAggregate(template: Template, spec: Omit<Aggregate, 'aggregate_id'>, id?: string): { template: Template; id: string } {
  const aggregates = aggregatesOf(template);
  const aggregateId = id ?? slugId((spec.label as Localized)['fr'] ?? 'total', new Set(aggregates.map((aggregate) => aggregate.aggregate_id)), 'total');
  const aggregate = compact({ aggregate_id: aggregateId, ...spec } as Aggregate);
  return { template: updateLayout(template, (layout) => ({ ...layout, aggregates: [...aggregates, aggregate] })), id: aggregateId };
}

export function updateAggregate(template: Template, aggregateId: string, patch: Patch<Aggregate>): Template {
  return updateLayout(template, (layout) => ({ ...layout, aggregates: aggregatesOf(template).map((aggregate) => (aggregate.aggregate_id === aggregateId ? (compact({ ...aggregate, ...patch }) as Aggregate) : aggregate)) }));
}

/** Retire un total ; les compteurs de page qui le citaient disparaissent avec lui. */
export function removeAggregate(template: Template, aggregateId: string): Template {
  const kept = aggregatesOf(template).filter((aggregate) => aggregate.aggregate_id !== aggregateId);
  return updateLayout(template, (layout) => ({
    ...withPages(layout, declaredPages(template).map((page) => (page.counter?.aggregate_ref === aggregateId ? compact({ ...page, counter: undefined } as unknown as LayoutPage) : page))),
    aggregates: kept.length === 0 ? undefined : kept,
  }) as FormLayout);
}

// ---- Noms internes (mode développeur seulement : jamais montrés à l'intégrateur).

/** Renomme l'identifiant interne d'une page, d'un bloc ou d'un total, et toutes ses citations. */
export function renameId(template: Template, kind: 'page' | 'block' | 'aggregate', from: string, to: string): Template {
  if (from === to || !/^[a-z][a-z0-9_]*$/.test(to)) return template;
  return updateLayout(template, (layout) => {
    const swap = (value: string): string => (value === from ? to : value);
    if (kind === 'page') {
      return { ...layout, pages: layout.pages?.map((page) => ({ ...page, page_id: swap(page.page_id) })), blocks: layout.blocks?.map((block) => compact({ ...block, page_ids: block.page_ids?.map(swap) })) } as FormLayout;
    }
    if (kind === 'block') {
      const footer = layout.footer === undefined ? undefined : (Object.fromEntries(Object.entries(layout.footer).map(([slot, value]) => [slot, typeof value === 'string' && (slot === 'start' || slot === 'center' || slot === 'end') ? swap(value) : value])) as NonNullable<FormLayout['footer']>);
      return { ...layout, blocks: layout.blocks?.map((block) => ({ ...block, block_id: swap(block.block_id) })), footer } as FormLayout;
    }
    const renamed = { ...layout, aggregates: layout.aggregates?.map((aggregate) => ({ ...aggregate, aggregate_id: swap(aggregate.aggregate_id) })), pages: layout.pages?.map((page) => (page.counter === undefined ? page : { ...page, counter: { aggregate_ref: swap(page.counter.aggregate_ref) } })) } as FormLayout;
    // Les textes composés et les liaisons citent `$aggregates.<id>` : la citation suit le nouveau nom.
    return replaceReference(renamed, `$aggregates.${from}`, `$aggregates.${to}`);
  });
}

function replaceReference<T>(value: T, from: string, to: string): T {
  const walk = (node: unknown): unknown => {
    if (typeof node === 'string') return node === from || node.startsWith(`${from}.`) ? `${to}${node.slice(from.length)}` : node;
    if (Array.isArray(node)) return node.map(walk);
    if (node !== null && typeof node === 'object') return Object.fromEntries(Object.entries(node).map(([key, entry]) => [key, walk(entry)]));
    return node;
  };
  return walk(value) as T;
}
